<template>
  <div class="min-h-screen bg-surface">
    <!-- Hero Section -->
    <Section class="pt-24 pb-16 bg-white">
      <div class="container mx-auto px-4 max-w-4xl">
        <h1 class="text-4xl md:text-5xl font-bold text-ink mb-6 text-center">
          Privacy Policy
        </h1>
        <p class="text-lg text-muted text-center mb-8">
          Last updated: April 1, 2024
        </p>
      </div>
    </Section>

    <!-- Content Section -->
    <Section class="py-16">
      <div class="container mx-auto px-4 max-w-4xl">
        <div class="space-y-12">
          <!-- Overview -->
          <div>
            <h2 class="text-2xl font-bold text-ink mb-4">Overview</h2>
            <p class="text-muted">We value your privacy and are committed to protecting your personal information. This Privacy Policy explains what information we collect, how we use it, your legal rights, and how to exercise them, in accordance with the General Data Protection Regulation and the California Consumer Privacy Act.</p>
          </div>

          <!-- Information We Collect -->
          <div>
            <h2 class="text-2xl font-bold text-ink mb-4">Information We Collect</h2>
            <ul class="list-disc list-inside text-muted space-y-2">
              <li><strong>Account Information:</strong> Name, email address, company name</li>
              <li><strong>Marketplace Data:</strong> Store identifiers, API tokens</li>
              <li><strong>Content Data:</strong> Product listings, images, media files</li>
              <li><strong>Advertising & Analytics:</strong> Search terms, CTR, conversion rates, ad spend reports</li>
              <li><strong>Usage Information:</strong> IP address, device/browser type, interaction logs</li>
            </ul>
          </div>

          <!-- How We Use Your Data -->
          <div>
            <h2 class="text-2xl font-bold text-ink mb-4">How We Use Your Data</h2>
            <ul class="list-disc list-inside text-muted space-y-2">
              <li>Provide, maintain, and improve our services</li>
              <li>Sync listings across integrated marketplaces like Amazon, Shopify, Walmart, and Target</li>
              <li>Generate performance reports and actionable insights</li>
              <li>Recommend optimizations using advertising and search analytics</li>
              <li>Communicate with you about product updates, security, and support</li>
            </ul>
            <h3 class="text-xl font-semibold text-ink mt-6 mb-3">Legal Basis for Processing</h3>
            <ul class="list-disc list-inside text-muted space-y-2">
              <li>Your consent where applicable</li>
              <li>Contractual necessity to deliver our service</li>
              <li>Legitimate interests to improve services and maintain security</li>
              <li>Legal obligation where required by law</li>
            </ul>
          </div>

          <!-- Data Sharing and Disclosure -->
          <div>
            <h2 class="text-2xl font-bold text-ink mb-4">Data Sharing and Disclosure</h2>
            <ul class="list-disc list-inside text-muted space-y-2">
              <li>With third-party platforms you connect like Amazon and Shopify</li>
              <li>With service providers acting as data processors under contract</li>
              <li>To comply with legal obligations or enforce our agreements</li>
            </ul>
          </div>

          <!-- Data Retention -->
          <div>
            <h2 class="text-2xl font-bold text-ink mb-4">Data Retention</h2>
            <p class="text-muted">We retain your personal data only as long as necessary for the purposes stated above or to comply with legal obligations. You may request deletion of your data at any time.</p>
          </div>

          <!-- Data Security -->
          <div>
            <h2 class="text-2xl font-bold text-ink mb-4">Data Security</h2>
            <ul class="list-disc list-inside text-muted space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Access controls and audit logging</li>
              <li>Regular security monitoring and review</li>
            </ul>
          </div>

          <!-- Your Rights -->
          <div>
            <h2 class="text-2xl font-bold text-ink mb-4">Your Rights</h2>
            <h3 class="text-xl font-semibold text-ink mb-3">Under GDPR - EU Residents</h3>
            <ul class="list-disc list-inside text-muted space-y-2 mb-6">
              <li>Access, correct, or delete your personal data</li>
              <li>Object to or restrict data processing</li>
              <li>Data portability to receive your data in a usable format</li>
              <li>Withdraw consent at any time where applicable</li>
              <li>File a complaint with your data protection authority</li>
            </ul>
            <h3 class="text-xl font-semibold text-ink mb-3">Under CCPA/CPRA - California Residents</h3>
            <ul class="list-disc list-inside text-muted space-y-2">
              <li>Know what personal information is collected and how it is used</li>
              <li>Request access to or deletion of your data</li>
              <li>Opt-out of sale or sharing of your data. We do not sell your data.</li>
              <li>Correct inaccurate personal data</li>
              <li>Limit use of sensitive personal data. We do not collect it.</li>
            </ul>
            <p class="text-muted mt-6">To exercise these rights, contact us at privacy@easytomanage.com.</p>
          </div>

          <!-- International Data Transfers -->
          <div>
            <h2 class="text-2xl font-bold text-ink mb-4">International Data Transfers</h2>
            <p class="text-muted">Your data may be processed in the U.S. We use appropriate safeguards including Standard Contractual Clauses to comply with GDPR.</p>
          </div>

          <!-- Use of Amazon Data -->
          <div>
            <h2 class="text-2xl font-bold text-ink mb-4">Use of Amazon Data</h2>
            <ul class="list-disc list-inside text-muted space-y-2">
              <li>We do not aggregate, combine, or analyze Amazon data across different businesses or customers.</li>
              <li>We do not sell, share, or provide any Amazon data or derived insights to third parties, including other Amazon users.</li>
              <li>Amazon data is used only to provide services to the specific authorized account from which the data originated, in accordance with Amazon Developer Terms and all applicable policies.</li>
            </ul>
          </div>

          <!-- Changes to This Policy -->
          <div>
            <h2 class="text-2xl font-bold text-ink mb-4">Changes to This Policy</h2>
            <p class="text-muted">We may update this policy from time to time. Material changes will be communicated via email or service notifications.</p>
          </div>

          <!-- Contact Us -->
          <div class="bg-gray-50 p-6 rounded-lg">
            <h2 class="text-2xl font-bold text-ink mb-4">Contact Us</h2>
            <p class="text-muted mb-3">Email: <a href="mailto:privacy@easytomanage.com" class="text-brand hover:underline">privacy@easytomanage.com</a></p>
            <p class="text-muted">Address: 681 Fifth Avenue, 14th Floor, New York, NY 10022</p>
          </div>
        </div>
      </div>
    </Section>
  </div>
</template>

<script setup lang="ts">
import Section from '@/components/Section.vue'
</script>